import React, { useMemo } from 'react';
import { ConfigProvider, Checkbox, type CheckboxChangeEvent } from '@app/ui';
import CardVideo from './CardVideo';
import CardImage from './CardImage';
import CardText from './CardText';
import isImage from '@app/utils/isImage';
import classNames from '@app/ui/sports-components/utils/classNames';
import './SocialPost.scss';
import SocialPostCard from './SocialPostCard';
import { useGridPostSelected } from '@app/components/social-posts/selectionStore';

type SocialPostProps = {
    src?: string;
    postUrl?: string;
    sourceType: string;
    createdAt?: string | number | Date;
    isVideo?: boolean;
    isCarousel?: boolean;
    isSelected?: boolean;
    isSelectMode?: boolean;
    // selectionType?: string;
    width?: string | number;
    height?: string | number;
    sponsorMediaValue?: number;
    currency?: string;
    socialAccount?: { username?: string | null } | null;
    content?: string;
    isTextPost?: boolean;
    onMediaClick?: () => void;
    onSelect?: (e: CheckboxChangeEvent) => void;
    collectionNames?: Array<string | undefined>;
    selectionIds?: Array<string | number>;
};

const SocialPost: React.FC<SocialPostProps> = ({
    src,
    postUrl = '',
    sourceType,
    createdAt,
    isVideo = false,
    isCarousel = false,
    isSelected = false,
    isSelectMode = false,
    // selectionType,
    width,
    height,
    sponsorMediaValue,
    currency,
    socialAccount,
    content,
    isTextPost = false,
    onMediaClick,
    onSelect,
    collectionNames,
    selectionIds,
}) => {
    const isChecked = useGridPostSelected(selectionIds, isSelected);
    const hasMedia = isVideo || (src ? isImage(src) : false);
    const accountName = socialAccount?.username;
    const hasAccountName = !!accountName;
    const lowerSocName =
        sourceType === 'Twitter' || sourceType === 'X'
            ? 'x-twitter'
            : sourceType.toLowerCase();
    const socMediaHandle =
        hasAccountName && lowerSocName !== 'youtube'
            ? `@${accountName}`
            : accountName || undefined;
    const formattedPostUrl =
        lowerSocName === 'instagram' && !postUrl.endsWith('/')
            ? `${postUrl}/`
            : postUrl;

    const mediaContainerClasses = classNames(
        'photos-container',
        '-cursor--pointer',
        {
            '-pointer-events--none': isSelectMode,
        },
    );

    const canSelect = isSelectMode;

    const card = useMemo(() => {
        if (isTextPost) {
            return (
                <CardText
                    className={mediaContainerClasses}
                    content={content}
                    onMediaClick={onMediaClick}
                />
            );
        }
        return isVideo && !isImage(src ?? '') ? (
            <CardVideo
                className={mediaContainerClasses}
                url={src ?? ''}
                width={
                    typeof width === 'number' && !isNaN(width)
                        ? width
                        : undefined
                }
                height={
                    typeof height === 'number' && !isNaN(height)
                        ? height
                        : undefined
                }
                onClick={onMediaClick}
                enableHoverPreview={!isSelectMode}
            />
        ) : (
            <CardImage
                className={mediaContainerClasses}
                url={src ?? ''}
                width={
                    typeof width === 'number' && !isNaN(width)
                        ? width
                        : undefined
                }
                height={
                    typeof height === 'number' && !isNaN(height)
                        ? height
                        : undefined
                }
                onClick={onMediaClick}
                hasMedia={hasMedia}
            />
        );
    }, [
        isTextPost,
        isVideo,
        src,
        width,
        height,
        onMediaClick,
        hasMedia,
        mediaContainerClasses,
        content,
        isSelectMode,
    ]);

    return (
        <div
            className={classNames('preview-container', {
                'is-selected': isChecked,
            })}
            data-test="social-post"
        >
            {canSelect && (
                <div
                    className={classNames('social-post-checkbox-v2', {
                        'is-visible': isChecked,
                    })}
                >
                    <Checkbox
                        checked={isChecked}
                        onChange={e => {
                            e.stopPropagation();
                            onSelect?.(e);
                        }}
                        data-test="post-toggle"
                    />
                </div>
            )}
            <div
                className="selectable-card-wrapper"
                onClick={e => {
                    if (!canSelect) return;
                    e.stopPropagation();
                    onSelect?.({
                        target: {
                            checked: !isChecked,
                        },
                    } as CheckboxChangeEvent);
                }}
            >
                <ConfigProvider
                    theme={{
                        components: {
                            Card: {
                                colorBorderSecondary: '#D4D4D4',
                                bodyPadding: 12,
                            },
                        },
                    }}
                >
                    <SocialPostCard
                        card={card}
                        width={width}
                        height={height}
                        socMediaHandle={socMediaHandle}
                        isVideo={isVideo}
                        isCarousel={isCarousel}
                        formattedPostUrl={formattedPostUrl}
                        createdAt={createdAt}
                        sponsorMediaValue={sponsorMediaValue}
                        currency={currency}
                        lowerSocName={lowerSocName}
                        sourceType={sourceType}
                        collectionNames={collectionNames}
                        isTextPost={isTextPost}
                        content={content}
                    />
                </ConfigProvider>
            </div>
        </div>
    );
};

const listsEqual = <T,>(prev?: T[], next?: T[]) => {
    if (prev === next) return true;
    if (!prev || !next || prev.length !== next.length) return false;
    for (let index = 0; index < prev.length; index += 1) {
        if (prev[index] !== next[index]) return false;
    }
    return true;
};

export const areSocialPostPropsEqual = (
    prev: SocialPostProps,
    next: SocialPostProps,
) =>
    prev.isSelected === next.isSelected &&
    prev.isSelectMode === next.isSelectMode &&
    prev.isCarousel === next.isCarousel &&
    prev.isVideo === next.isVideo &&
    prev.isTextPost === next.isTextPost &&
    prev.postUrl === next.postUrl &&
    prev.src === next.src &&
    prev.createdAt === next.createdAt &&
    prev.sponsorMediaValue === next.sponsorMediaValue &&
    prev.currency === next.currency &&
    prev.content === next.content &&
    prev.width === next.width &&
    prev.height === next.height &&
    prev.sourceType === next.sourceType &&
    prev.socialAccount?.username === next.socialAccount?.username &&
    listsEqual(prev.collectionNames, next.collectionNames) &&
    listsEqual(prev.selectionIds, next.selectionIds);

export default React.memo(SocialPost, areSocialPostPropsEqual);
